import { AlertTriangle, CheckCircle2, Pencil, PlayCircle, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GuideEditor } from "@/components/guide/GuideEditor";
import { imageUrl } from "@/components/guide/GuideContext";
import { runTour } from "@/components/guide/tour";

function useChecks(slug) {
  const key = `guide-checks-${slug}`;
  const [done, setDone] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  const save = (v) => { setDone(v); localStorage.setItem(key, JSON.stringify(v)); };
  return [done, (i) => save(done.includes(i) ? done.filter((x) => x !== i) : [...done, i]), () => save([])];
}

function Steps({ steps, onZoom }) {
  return (
    <ol className="guide-steps">
      {steps.map((s, i) => (
        <li key={i} data-testid={`guide-step-${i}`}>
          <p>{s.text}</p>
          {s.image && <button type="button" className="guide-shot" onClick={() => onZoom(s.image)} title="Agrandir" data-testid={`guide-step-image-${i}`}><img src={imageUrl(s.image)} alt={`Étape ${i + 1}`} loading="lazy" /></button>}
        </li>
      ))}
    </ol>
  );
}

function Checks({ slug, checks }) {
  const [done, toggle, reset] = useChecks(slug);
  return (
    <section className="guide-block guide-checks" data-testid="guide-checks">
      <h4><CheckCircle2 size={16} />Vérifier que ça marche <small>{done.length}/{checks.length}</small>{done.length > 0 && <button type="button" className="link-button" onClick={reset} data-testid="guide-checks-reset"><RotateCcw size={12} />Recommencer</button>}</h4>
      {checks.map((c, i) => (
        <label key={i} className={done.includes(i) ? "is-done" : ""} data-testid={`guide-check-${i}`}>
          <input type="checkbox" checked={done.includes(i)} onChange={() => toggle(i)} data-testid={`guide-check-box-${i}`} />
          <span><b>{c.test}</b><em>→ {c.expected}</em></span>
        </label>
      ))}
    </section>
  );
}

export function GuideFiche({ page, admin, onClose, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [zoom, setZoom] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (editing) return <GuideEditor page={page} onDone={() => { setEditing(false); onSaved(); }} />;
  const startTour = () => {
    onClose();
    if (!pathname.startsWith(page.screen)) navigate(page.screen);
    setTimeout(() => runTour(page.tour), 400);
  };
  return (
    <article className="guide-fiche" data-testid={`guide-fiche-${page.slug}`}>
      <p className="guide-group">{page.group}</p>
      <h3>{page.title}</h3>
      <div className="guide-actions">
        {page.tour?.length > 0 && <button type="button" className="primary-button" onClick={startTour} data-testid="guide-fiche-tour"><PlayCircle size={15} />Lancer la visite</button>}
        {admin && <button type="button" className="secondary-button" onClick={() => setEditing(true)} data-testid="guide-fiche-edit"><Pencil size={14} />Modifier</button>}
      </div>
      <section className="guide-block"><h4>À quoi ça sert</h4><p>{page.purpose}</p></section>
      <section className="guide-block"><h4>Comment l'utiliser</h4><Steps steps={page.steps} onZoom={setZoom} /></section>
      {page.checks?.length > 0 && <Checks slug={page.slug} checks={page.checks} />}
      {page.limits?.length > 0 && <section className="guide-block guide-limits" data-testid="guide-limits"><h4><AlertTriangle size={15} />Limites connues</h4><ul>{page.limits.map((l, i) => <li key={i}>{l}</li>)}</ul></section>}
      {page.updated_by && <p className="guide-muted">Modifié par {page.updated_by} le {new Date(page.updated_at).toLocaleDateString("fr-FR")}</p>}
      {zoom && <button type="button" className="guide-zoom" onClick={() => setZoom(null)} data-testid="guide-zoom"><img src={imageUrl(zoom)} alt="Capture agrandie" /></button>}
    </article>
  );
}
